import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Modal from '@/components/common/Modal';
import { 
  FaCar, 
  FaUser, 
  FaCalendar, 
  FaTools, 
  FaCog, 
  FaTag, 
  FaFileAlt, 
  FaUserTie,
  FaDownload,
  FaPhone,
  FaEnvelope,
  FaPalette,
  FaClock,
  FaEdit,
  FaTachometerAlt,
  FaImages,
  FaSpinner
} from 'react-icons/fa';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import toastService from '@/utils/toastService';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import { ImageComparison } from '@/components/images';
import ImageGalleryWithViewer from '@/components/images/ImageGalleryWithViewer';
import CitaImagenesApiService from '@/services/citaImagenesApiService';
import MantenimientoImagenesApiService from '@/services/mantenimientoImagenesApiService';
import { useSession } from '@clerk/clerk-react';

/**
 * Modal para mostrar el detalle completo de un mantenimiento
 */
const MantenimientoDetailModal = ({ isOpen, onClose, mantenimiento }) => {
  const [mantenimientoDetalle, setMantenimientoDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Estado para imágenes
  const [imagenesAntes, setImagenesAntes] = useState([]);
  const [imagenesDespues, setImagenesDespues] = useState([]);
  const [loadingImagenes, setLoadingImagenes] = useState(false);
  
  
  const { session } = useSession();

  // Cargar detalle completo cuando se abre el modal
  useEffect(() => {
    if (isOpen && mantenimiento) {
      cargarDetalle();
      cargarImagenes();
    }
  }, [isOpen, mantenimiento]);

  // Cargar imágenes previas y posteriores
  const cargarImagenes = async () => {
    if (!mantenimiento?.id) return;
    
    setLoadingImagenes(true);
    try {
      // Primero necesitamos obtener el cita_id desde el detalle del mantenimiento
      const detalle = await mantenimientosApiService.getMantenimientoById(mantenimiento.id);
      const citaId = detalle?.cita?.id;
      
      if (citaId) {
        // Cargar imágenes previas (de la cita)
        const imagenesAntesResult = await CitaImagenesApiService.obtenerImagenesCita(session, citaId);
        setImagenesAntes(imagenesAntesResult);
      }
      
      // Cargar imágenes Posterior (del mantenimiento)
      const imagenesDespuesResult = await MantenimientoImagenesApiService.obtenerImagenesMantenimiento(session, mantenimiento.id);
      setImagenesDespues(imagenesDespuesResult);
      
    } catch (error) {
      console.error('Error cargando imágenes:', error);
      // No mostrar error toast para no interferir con la UX
    } finally {
      setLoadingImagenes(false);
    }
  };

  const cargarDetalle = async () => {
    if (!mantenimiento?.id) return;
    
    setLoading(true);
    try {
      const detalle = await mantenimientosApiService.getMantenimientoById(mantenimiento.id);
      
      if (!detalle) {
        throw new Error('No se encontraron datos del mantenimiento');
      }
      
      //console.log('Detalle del mantenimiento (API):', detalle);
      setMantenimientoDetalle(detalle);
    } catch (error) {
      console.error('Error cargando detalle del mantenimiento:', error);
      toastService.error('No se pudo cargar el detalle del mantenimiento');
      setMantenimientoDetalle(null);
    } finally {
      setLoading(false);
    }
  };

  // Formatear precio
  const formatPrice = (price) => {
    // Manejar diferentes tipos de entrada
    if (price === null || price === undefined || price === '') {
      return '0';
    }
    
    // Convertir a número manteniendo el signo
    const numero = parseFloat(price) || 0;
    const esNegativo = numero < 0;
    const numeroAbsoluto = Math.abs(numero);
    
    // Formatear manualmente para asegurar separador de miles
    const numeroStr = numeroAbsoluto.toString();
    const partes = numeroStr.split('.');
    const entero = partes[0];
    
    // Agregar separadores de miles manualmente
    const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Devolver con signo negativo si corresponde
    return esNegativo ? `-${enteroFormateado}` : enteroFormateado;
  };

  // Ver PDF
  const handleVerPDF = () => {
    if (mantenimientoDetalle?.pdf_base64) {
      const base64Data = mantenimientoDetalle.pdf_base64.split(',')[1] || mantenimientoDetalle.pdf_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      toastService.warning('No hay PDF disponible para este mantenimiento');
    }
  };

  // Descargar PDF
  const handleDescargarPDF = () => {
    if (mantenimientoDetalle?.pdf_base64) {
      const base64Data = mantenimientoDetalle.pdf_base64.split(',')[1] || mantenimientoDetalle.pdf_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Garantia_${mantenimientoDetalle.patente}_${moment(mantenimientoDetalle.fecha).format('DDMMYYYY')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      toastService.warning('No hay PDF disponible para descargar');
    }
  };


  const cliente = mantenimientoDetalle?.cita?.cliente;
  const vehiculo = mantenimientoDetalle?.cita?.vehiculo;

  const modalTitle = (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <span>Detalle del Mantenimiento</span>
        {mantenimientoDetalle && (
          <p className="text-gray-400 text-sm font-normal mt-1">
            {moment(mantenimientoDetalle.fecha).format('DD/MM/YYYY')}
          </p>
        )}
      </div>
      {mantenimientoDetalle && (
        <div className="flex items-center">
          <PatenteDisplay patente={mantenimientoDetalle.patente} size="sm" />
        </div>
      )}
    </div>
  );

  const modalFooter = (
    <div className="flex justify-between items-center">
      {/* Botón Ver PDF - lado izquierdo */}
      {mantenimientoDetalle?.pdf_base64 ? (
        <button
          onClick={handleVerPDF}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base"
          title="Ver PDF"
        >
          <FaFileAlt className="text-sm" />
          <span className="text-sm font-medium">Documento de Garantía</span>
          <FaDownload className="text-xs" />
        </button>
      ) : (
        <div></div>
      )}
      
      {/* Botón Cerrar - lado derecho */}
      <button
        onClick={onClose}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base"
      >
        Cerrar
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="2xl"
      footer={modalFooter}
    >
      {loading ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-2">Cargando detalle...</p>
        </div>
      ) : mantenimientoDetalle ? (
        <div className="flex flex-col h-full">
          {/* Tabs - Sticky */}
          <div className="sticky-fix top-0 z-10 border-b border-gray-700 flex-shrink-0 bg-gray-800 p-1 -mx-4 -mt-4 mb-4">
            <nav className="flex space-x-1">
              {[
                { id: 'general', label: 'Información', shortLabel: 'Info', icon: FaFileAlt },
                { id: 'servicios', label: 'Servicios', icon: FaTools },
                { id: 'repuestos', label: 'Repuestos', icon: FaCog },
                { id: 'imagenes', label: 'Imágenes', icon: FaImages },
                { id: 'costos', label: 'Costos', icon: FaTag }
              ].map(tab => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 px-1 sm:px-4 rounded-lg 
                      text-xs sm:text-sm font-medium transition-all duration-200 border
                      ${isActive
                        ? 'bg-blue-600 text-white border-blue-500 shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700 border-transparent hover:border-gray-600'
                      }
                    `}
                  >
                    <IconComponent className="text-sm flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate">
                      {tab.shortLabel || tab.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
                {/* Información General */}
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    {/* Primera fila: Cliente y Vehículo */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Cliente */}
                      <div className="border border-gray-600 rounded-lg p-3">
                        <h3 className="text-base font-medium text-white mb-3 flex items-center">
                          <FaUser className="mr-2 text-green-400" />
                          Información del Cliente
                        </h3>
                        {/* Línea divisoria */}
                        <div className="border-t border-gray-600 mb-3"></div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center">
                            <FaUser className="w-3 h-3 text-gray-500 mr-2" />
                            <span className="text-gray-400">Nombre:</span>
                            <span className="text-white ml-2">
                              {cliente ? `${cliente.nombre} ${cliente.apellido}` : 'No disponible'}
                            </span>
                          </div>
                          {cliente?.telefono && (
                            <div className="flex items-center">
                              <FaPhone className="w-3 h-3 text-gray-500 mr-2" />
                              <span className="text-gray-400">Teléfono:</span>
                              <span className="text-white ml-2">{cliente.telefono}</span>
                            </div>
                          )}
                          {cliente?.email && (
                            <div className="flex items-center">
                              <FaEnvelope className="w-3 h-3 text-gray-500 mr-2" />
                              <span className="text-gray-400">Email:</span>
                              <span className="text-white ml-2">{cliente.email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Vehículo */}
                      <div className="border border-gray-600 rounded-lg p-3">
                        <h3 className="text-base font-medium text-white mb-3 flex items-center">
                          <FaCar className="mr-2 text-blue-400" />
                          Información del Vehículo
                        </h3>
                        {/* Línea divisoria */}
                        <div className="border-t border-gray-600 mb-3"></div>
                        <div className="space-y-1 text-sm">
                          {vehiculo && (
                            <>
                              <div className="flex items-center">
                                <FaCar className="w-3 h-3 text-gray-500 mr-2" />
                                <span className="text-gray-400">Marca/Modelo:</span>
                                <span className="text-white ml-2">{vehiculo.marca_local} {vehiculo.modelo_local}</span>
                              </div>
                              <div className="flex justify-between">
                                <div className="flex items-center">
                                  <FaCalendar className="w-3 h-3 text-gray-500 mr-2" />
                                  <span className="text-gray-400">Año:</span>
                                  <span className="text-white ml-2">{vehiculo.año}</span>
                                </div>
                                {vehiculo.color && (
                                  <div className="flex items-center">
                                    <FaPalette className="w-3 h-3 text-gray-500 mr-2" />
                                    <span className="text-gray-400">Color:</span>
                                    <span className="text-white ml-2">{vehiculo.color}</span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          <div className="flex items-center">
                            <FaTachometerAlt className="w-3 h-3 text-gray-500 mr-2" />
                            <span className="text-gray-400">Kilometraje:</span>
                            <span className="text-white ml-2">{mantenimientoDetalle.kilometraje_actual?.toLocaleString()} km</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Segunda fila: Servicio y Mecánicos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Información del Servicio */}
                      <div className="border border-gray-600 rounded-lg p-3">
                        <h3 className="text-base font-medium text-white mb-3 flex items-center">
                          <FaCalendar className="mr-2 text-yellow-400" />
                          Información del Servicio
                        </h3>
                        {/* Línea divisoria */}
                        <div className="border-t border-gray-600 mb-3"></div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center">
                            <FaCalendar className="w-3 h-3 text-gray-500 mr-2" />
                            <span className="text-gray-400">Fecha de servicio:</span>
                            <span className="text-white ml-2">
                              {moment(mantenimientoDetalle.fecha).format('DD/MM/YYYY')}
                            </span>
                          </div>
                          {mantenimientoDetalle.hora_finalizacion && (
                            <div className="flex items-center">
                              <FaClock className="w-3 h-3 text-gray-500 mr-2" />
                              <span className="text-gray-400">Hora finalización:</span>
                              <span className="text-white ml-2">{mantenimientoDetalle.hora_finalizacion}</span>
                            </div>
                          )}
                          <div className="flex items-center">
                            <FaEdit className="w-3 h-3 text-gray-500 mr-2" />
                            <span className="text-gray-400">Actualización:</span>
                            <span className="text-white ml-2">
                              {moment(mantenimientoDetalle.updated_at).format('DD/MM/YYYY HH:mm')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Mecánicos */}
                      <div className="border border-gray-600 rounded-lg p-3">
                        <h3 className="text-base font-medium text-white mb-3 flex items-center">
                          <FaUserTie className="mr-2 text-purple-400" />
                          Mecánicos Asignados
                        </h3>
                        {/* Línea divisoria */}
                        <div className="border-t border-gray-600 mb-3"></div>
                        <div className="space-y-1 text-sm">
                          {mantenimientoDetalle.mecanicos?.length > 0 ? (
                            mantenimientoDetalle.mecanicos.map((mecanicoItem, index) => (
                              <div key={index} className="flex justify-between">
                                <div className="flex items-center">
                                  <FaUserTie className="w-3 h-3 text-gray-500 mr-2" />
                                  <span className="text-white">{mecanicoItem.mecanico?.nombre}</span>
                                </div>
                                <span className="text-gray-400 text-xs">{mecanicoItem.rol}</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center">
                              <FaUserTie className="w-3 h-3 text-gray-500 mr-2" />
                              <span className="text-gray-400">Sin mecánicos asignados</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Observaciones - ancho completo */}
                    {mantenimientoDetalle.descripcion && (
                      <div className="border border-gray-600 rounded-lg p-3">
                        <h3 className="text-base font-medium text-white mb-3 flex items-center">
                          <FaFileAlt className="mr-2 text-orange-400" />
                          Observaciones
                        </h3>
                        {/* Línea divisoria */}
                        <div className="border-t border-gray-600 mb-3"></div>
                        <p className="text-gray-300 text-sm">{mantenimientoDetalle.descripcion}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Servicios */}
                {activeTab === 'servicios' && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <FaTools className="mr-2 text-blue-400" />
                      Servicios Realizados
                    </h3>
                    
                    {mantenimientoDetalle.servicios?.length > 0 ? (
                      <>
                        <div className="grid gap-4">
                          {mantenimientoDetalle.servicios.map((servicioItem, index) => (
                            <div key={index} className="border border-gray-600 p-4 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium text-white">
                                    {servicioItem.servicio?.nombre || 'Servicio sin nombre'}
                                  </h4>
                                  {servicioItem.servicio?.descripcion && (
                                    <p className="text-gray-400 text-sm mt-1">
                                      {servicioItem.servicio.descripcion}
                                    </p>
                                  )}
                                  {servicioItem.observaciones && (
                                    <p className="text-gray-300 text-sm mt-2">
                                      <strong>Observaciones:</strong> {servicioItem.observaciones}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  <div className="text-lg font-medium text-white">
                                    ${formatPrice(servicioItem.precio)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="border-t border-gray-600 pt-3 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold">Total Servicios:</span>
                            <span className="text-green-400 font-bold text-lg">
                              ${formatPrice(mantenimientoDetalle.total_servicios)}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-400">No se registraron servicios para este mantenimiento.</p>
                    )}
                  </div>
                )}

                {/* Repuestos */}
                {activeTab === 'repuestos' && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <FaCog className="mr-2 text-orange-400" />
                      Repuestos Utilizados
                    </h3>
                    
                    {mantenimientoDetalle.repuestos?.length > 0 ? (
                      <>
                        <div className="grid gap-4">
                          {mantenimientoDetalle.repuestos.map((repuestoItem, index) => (
                            <div key={index} className="border border-gray-600 p-4 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium text-white">
                                    {repuestoItem.repuesto?.nombre || 'Repuesto sin nombre'}
                                  </h4>
                                  {repuestoItem.repuesto?.marca && (
                                    <p className="text-gray-400 text-sm">
                                      Marca: {repuestoItem.repuesto.marca}
                                    </p>
                                  )}
                                  <div className="text-gray-300 text-sm mt-1">
                                    Cantidad: {repuestoItem.cantidad}
                                  </div>
                                  {repuestoItem.observaciones && (
                                    <p className="text-gray-300 text-sm mt-2">
                                      <strong>Observaciones:</strong> {repuestoItem.observaciones}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  <div className="text-sm text-gray-400">
                                    ${formatPrice(repuestoItem.precio_unitario)} c/u
                                  </div>
                                  <div className="text-lg font-medium text-white">
                                    ${formatPrice(repuestoItem.precio_total)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="border-t border-gray-600 pt-3 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold">Total Repuestos:</span>
                            <span className="text-green-400 font-bold text-lg">
                              ${formatPrice(mantenimientoDetalle.total_repuestos)}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-400">No se utilizaron repuestos en este mantenimiento.</p>
                    )}

                  </div>
                )}

                {/* Imágenes */}
                {activeTab === 'imagenes' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-white flex items-center">
                        <FaImages className="mr-2 text-purple-400" />
                        Imágenes del Mantenimiento
                      </h3>
                      
                      <div className="flex items-center gap-3">
                        {loadingImagenes && (
                          <div className="flex items-center text-gray-400">
                            <FaSpinner className="animate-spin mr-2" size={14} />
                            <span className="text-sm">Cargando...</span>
                          </div>
                        )}
                        
                        {/* Stats cards - solo si hay imágenes y no están cargando */}
                        {!loadingImagenes && (imagenesAntes.length > 0 || imagenesDespues.length > 0) && (
                          <div className="flex gap-2">
                            {imagenesAntes.length > 0 && (
                              <div className="bg-blue-600 bg-opacity-20 border border-blue-500/30 rounded-lg py-2 w-[70px] flex flex-col items-center justify-center">
                                <div className="text-blue-400 text-lg font-bold">{imagenesAntes.length}</div>
                                <div className="text-blue-300 text-xs">Previo</div>
                              </div>
                            )}
                            {imagenesDespues.length > 0 && (
                              <div className="bg-green-600 bg-opacity-20 border border-green-500/30 rounded-lg py-2 w-[70px] flex flex-col items-center justify-center">
                                <div className="text-green-400 text-lg font-bold">{imagenesDespues.length}</div>
                                <div className="text-green-300 text-xs">Posterior</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {loadingImagenes ? (
                      <div className="text-center py-8">
                        <FaSpinner className="animate-spin mx-auto text-blue-400 mb-3" size={24} />
                        <p className="text-gray-400">Cargando imágenes...</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Mostrar comparador solo si hay imágenes antes Y después */}
                        {imagenesAntes.length > 0 && imagenesDespues.length > 0 ? (
                          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600/30">
                            <h4 className="text-white font-medium mb-4 flex items-center">
                              <FaImages className="mr-2 text-blue-400" />
                              Comparación Antes/Después
                            </h4>
                            <ImageComparison 
                              imagenesAntes={imagenesAntes}
                              imagenesDespues={imagenesDespues}
                            />
                          </div>
                        ) : (
                          <>
                            {/* Mostrar solo galería de imágenes previas si solo hay antes */}
                            {imagenesAntes.length > 0 && (
                              <ImageGalleryWithViewer
                                images={imagenesAntes}
                                title="Fotos Estado Previo"
                                showDeleteButton={false}
                                viewerTitle={`Fotos Estado Previo - ${mantenimientoDetalle.patente || 'Vehículo'}`}
                                className="bg-gray-800/50 border-gray-600/30"
                              />
                            )}

                            {/* Mostrar solo galería de imágenes posteriores si solo hay después */}
                            {imagenesDespues.length > 0 && (
                              <ImageGalleryWithViewer
                                images={imagenesDespues}
                                title="Fotos Estado Posterior"
                                showDeleteButton={false}
                                viewerTitle={`Fotos Estado Posterior - ${mantenimientoDetalle.patente || 'Vehículo'}`}
                                className="bg-gray-800/50 border-gray-600/30"
                              />
                            )}
                          </>
                        )}

                        {/* Mensaje si no hay imágenes */}
                        {imagenesAntes.length === 0 && imagenesDespues.length === 0 && (
                          <div className="text-center py-8">
                            <FaImages className="mx-auto text-gray-500 mb-3" size={32} />
                            <p className="text-gray-400">No hay imágenes disponibles para este mantenimiento</p>
                            <p className="text-gray-500 text-sm mt-2">
                              Las imágenes se capturan durante el proceso de finalización del mantenimiento
                            </p>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                )}

                {/* Costos */}
                {activeTab === 'costos' && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">
                      Detalle de Costos
                    </h3>
                    
                    <div className="border border-gray-600 p-6 rounded-lg">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Subtotal Servicios:</span>
                          <span className="text-white">${formatPrice(mantenimientoDetalle.total_servicios)}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-300">Subtotal Repuestos:</span>
                          <span className="text-white">${formatPrice(mantenimientoDetalle.total_repuestos)}</span>
                        </div>
                        
                        {/* Items Adicionales - Mostrar detalle */}
                        {mantenimientoDetalle.items_adicionales && mantenimientoDetalle.items_adicionales.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-gray-300 font-medium pt-2">Items Adicionales:</div>
                            {mantenimientoDetalle.items_adicionales.map((item, index) => (
                              <div key={index} className="flex justify-between pl-4">
                                <span className="text-gray-400 text-sm">
                                  {item.descripcion || 'Item adicional'} ({item.cantidad || 1})
                                </span>
                                <span className={`text-sm ${item.precio_total < 0 ? 'text-red-400' : 'text-white'}`}>
                                  ${formatPrice(item.precio_total)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="border-t border-gray-600 pt-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Subtotal:</span>
                            <span className="text-white">${formatPrice(mantenimientoDetalle.subtotal)}</span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-300">IVA ({mantenimientoDetalle.porcentaje_iva}%):</span>
                          <span className="text-white">${formatPrice(mantenimientoDetalle.monto_iva)}</span>
                        </div>
                        
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-600 pt-4 mt-4">
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-white">Total:</span>
                        <span className="text-green-400">${formatPrice(mantenimientoDetalle.total)}</span>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      ) : (
        <div className="p-8 text-center">
          <p className="text-gray-400">No se pudo cargar el detalle del mantenimiento</p>
        </div>
      )}

      </Modal>
  );
};

export default MantenimientoDetailModal;